/**
 * Sınav Sonuç (aday görünümü) — Spec Bölüm 7.4 + 8 + S2.T8c + S3.4
 *
 * GET /api/public/attempts/:attemptId/result
 *
 * Görünüm:
 *  - Toplam skor + seviye etiketi
 *  - Konu bazlı bar chart
 *  - AI markdown rapor (Sprint 3'te aktif)
 *  - Sertifika (L2+) — verify linki + LinkedIn paylaş
 *  - CTA: kendi firman / paylaş
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { ReportMarkdown } from '../components/ReportMarkdown.js';

interface QuestionBreakdown {
  index: number;
  questionId: string;
  code: string | null;
  topic: string | null;
  subtopic: string | null;
  difficulty: string | null;
  format: string | null;
  questionText: string | null;
  options: Array<{ key: string; text: string; correct?: boolean }>;
  correctAnswer: string | null;
  explanation: string | null;
  commonMistakes: string[];
  maxPoints: number;
  answered: boolean;
  selectedOption: string | null;
  numericAnswer: number | null;
  isCorrect: boolean;
  pointsAwarded: number;
  errorPercentage: number | null;
  nearMissCategory: string | null;
  timeSpentSec: number;
}

interface AttemptResult {
  attemptId: string;
  totalScore: number | null;
  scoreLevel: string | null;
  topicScores: Record<string, number> | null;
  completedAt: string | null;
  status: string;
  candidate: { displayName: string };
  totalQuestions: number;
  reportMarkdown: string | null;
  reportGeneratedAt: string | null;
  certificate: { id: string; validUntil: string | null } | null;
  breakdown: QuestionBreakdown[];
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch & Tasarım Niyeti',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel SW',
  DT: 'Tasarım Teorisi',
};

const LEVEL_LABELS: Record<string, string> = {
  L0: 'Tanışma',
  L1_MINUS: 'Başlangıç',
  L1: 'CSWA Seviyesi',
  L2: 'CSWA Üzeri',
  L3: 'CSWP Seviyesi',
  L4: 'İleri Seviye',
};

const LEVEL_COLORS: Record<string, string> = {
  L0: 'text-slate-600 bg-slate-100',
  L1_MINUS: 'text-orange-700 bg-orange-50',
  L1: 'text-yellow-800 bg-yellow-50',
  L2: 'text-sky-800 bg-sky-50',
  L3: 'text-green-800 bg-green-50',
  L4: 'text-purple-800 bg-purple-50',
};

export function ExamResultPage(): JSX.Element {
  const params = useParams<{ attemptId: string }>();
  const attemptId = params.attemptId ?? '';
  const [data, setData] = useState<AttemptResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);

  // İlk yükte: ya state'te IN_PROGRESS → complete et, ya da result'ı çek
  useEffect(() => {
    if (!attemptId) {
      setError('Geçersiz oturum');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Önce state — IN_PROGRESS ise complete + result
        const state = await api.get<{ status: string }>(
          `/api/public/attempts/${attemptId}/state`,
        );
        if (cancelled) return;
        if (state.status === 'IN_PROGRESS') {
          try {
            await api.post(`/api/public/attempts/${attemptId}/complete`);
          } catch {
            // Continue — sonra result endpoint dener
          }
        }
        const result = await api.get<AttemptResult>(
          `/api/public/attempts/${attemptId}/result`,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Sonuçlar yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attemptId]);

  // AI rapor hazır olunca polling durur — yoksa 5 sn'de bir tekrar dener (max 20)
  useEffect(() => {
    if (!data) return;
    if (data.reportMarkdown) return;
    if (pollCount >= 20) return;
    const timer = setTimeout(async () => {
      try {
        const next = await api.get<AttemptResult>(
          `/api/public/attempts/${attemptId}/result`,
        );
        setData(next);
        setPollCount((c) => c + 1);
      } catch {
        setPollCount((c) => c + 1);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [data, pollCount, attemptId]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <Alert variant="danger">{error}</Alert>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <p className="text-slate-500">Sonuçlar hesaplanıyor…</p>
        </main>
      </div>
    );
  }

  const score = data.totalScore ?? 0;
  const level = data.scoreLevel ?? 'L0';
  const levelLabel = LEVEL_LABELS[level] ?? level;
  const levelColor = LEVEL_COLORS[level] ?? 'text-slate-600 bg-slate-100';
  const topicScores = data.topicScores ?? {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-12">
        <div className="mb-8 text-center">
          <p className="text-sm font-medium text-slate-500">
            {data.candidate.displayName}
          </p>
          <h1 className="mt-1 text-3xl font-bold text-slate-900">
            Sınav Sonucunuz
          </h1>
        </div>

        {/* Skor kartı */}
        <Card>
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-wider text-slate-500">
              Toplam Skor
            </p>
            <p className="mt-2 text-7xl font-bold text-blue-900">
              {score}
              <span className="text-3xl text-slate-400">/100</span>
            </p>
            <span
              className={`mt-3 inline-block rounded-full px-4 py-1 text-sm font-semibold ${levelColor}`}
            >
              {level} · {levelLabel}
            </span>
          </div>
        </Card>

        {/* Sertifika kartı (L2+) */}
        {data.certificate && (
          <CertificateBanner cert={data.certificate} candidateName={data.candidate.displayName} />
        )}

        {/* Konu bazlı breakdown */}
        {Object.keys(topicScores).length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Konu Bazlı Performans
            </h2>
            <div className="mt-4 space-y-3">
              {Object.entries(topicScores)
                .sort(([, a], [, b]) => b - a)
                .map(([topic, pct]) => (
                  <div key={topic}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">
                        {TOPIC_LABELS[topic] ?? topic}
                      </span>
                      <span className="font-mono text-slate-900">{pct}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background:
                            pct >= 75
                              ? '#15803d'
                              : pct >= 50
                              ? '#0369a1'
                              : pct >= 30
                              ? '#ca8a04'
                              : '#dc2626',
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        )}

        {/* AI Rapor */}
        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Kişiselleştirilmiş AI Rapor
            </h2>
            {data.reportGeneratedAt && (
              <span className="text-xs text-slate-500">
                {new Date(data.reportGeneratedAt).toLocaleString('tr-TR')}
              </span>
            )}
          </div>
          <div className="mt-3">
            {data.reportMarkdown ? (
              <ReportMarkdown text={data.reportMarkdown} />
            ) : (
              <div className="space-y-3">
                <Alert variant="info">
                  <strong>AI raporunuz hazırlanıyor.</strong> Cevaplarınız analiz
                  edilerek kişiselleştirilmiş rapor üretiliyor. Bu işlem
                  genellikle 30 saniye sürer — sayfa otomatik yenilenir.
                </Alert>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-900 border-t-transparent" />
                  <span>Rapor hazırlanıyor (kontrol {pollCount}/20)</span>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Soru-soru breakdown */}
        {data.breakdown.length > 0 && (
          <QuestionBreakdownSection breakdown={data.breakdown} />
        )}

        {/* CTA */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link
            href="/yonetici/kayit"
            className="flex items-center justify-center rounded-lg bg-blue-900 px-6 py-3 text-base font-medium text-white hover:bg-blue-800"
          >
            Kendi Firmama Da Yapmak İstiyorum
          </Link>
          <Link
            href="/"
            className="flex items-center justify-center rounded-lg border-2 border-blue-900 px-6 py-3 text-base font-medium text-blue-900 hover:bg-blue-50"
          >
            Ana Sayfa
          </Link>
        </div>
      </main>
    </div>
  );
}

function QuestionBreakdownSection({
  breakdown,
}: {
  breakdown: QuestionBreakdown[];
}): JSX.Element {
  const [filter, setFilter] = useState<'all' | 'wrong' | 'correct' | 'unanswered'>('all');

  const counts = {
    all: breakdown.length,
    correct: breakdown.filter((b) => b.answered && b.isCorrect).length,
    wrong: breakdown.filter((b) => b.answered && !b.isCorrect).length,
    unanswered: breakdown.filter((b) => !b.answered).length,
  };

  const filtered = breakdown.filter((b) => {
    if (filter === 'all') return true;
    if (filter === 'correct') return b.answered && b.isCorrect;
    if (filter === 'wrong') return b.answered && !b.isCorrect;
    return !b.answered;
  });

  return (
    <Card className="mt-6">
      <h2 className="text-lg font-semibold text-slate-900">
        Soru Soru İnceleme
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Her sorunun doğru cevabını ve açıklamasını gör. Yanlışlardan ders çıkar.
      </p>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {(
          [
            { key: 'all', label: 'Tümü', n: counts.all, cls: 'bg-slate-100 text-slate-700' },
            { key: 'wrong', label: 'Yanlış', n: counts.wrong, cls: 'bg-red-50 text-red-700' },
            { key: 'correct', label: 'Doğru', n: counts.correct, cls: 'bg-green-50 text-green-700' },
            { key: 'unanswered', label: 'Boş', n: counts.unanswered, cls: 'bg-amber-50 text-amber-700' },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 font-medium ${
              filter === f.key
                ? 'bg-blue-900 text-white'
                : `${f.cls} hover:opacity-80`
            }`}
          >
            {f.label} ({f.n})
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500">Bu filtrede soru yok.</p>
        ) : (
          filtered.map((q) => <BreakdownRow key={q.questionId} q={q} />)
        )}
      </div>
    </Card>
  );
}

function BreakdownRow({ q }: { q: QuestionBreakdown }): JSX.Element {
  const [open, setOpen] = useState(false);
  const status = !q.answered
    ? { label: 'Cevaplanmadı', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
    : q.isCorrect
    ? { label: '✓ Doğru', cls: 'bg-green-50 text-green-700 border-green-200' }
    : { label: '✗ Yanlış', cls: 'bg-red-50 text-red-700 border-red-200' };

  return (
    <div className={`rounded-lg border ${status.cls} bg-white`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 p-3 text-left hover:bg-slate-50"
      >
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">
          {q.index}
        </span>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${status.cls}`}>
              {status.label}
            </span>
            {q.topic && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-900">
                {q.topic} · {q.difficulty}
              </span>
            )}
            <span className="text-xs text-slate-500">
              {q.pointsAwarded}/{q.maxPoints} puan
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-slate-800">
            {q.questionText}
          </p>
        </div>
        <span className="ml-2 mt-1 text-slate-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-slate-200 bg-slate-50/50 p-4 text-sm">
          <p className="font-medium text-slate-900">{q.questionText}</p>

          {q.options.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {q.options.map((o) => {
                const isCorrect = o.key === q.correctAnswer;
                const isSelected = o.key === q.selectedOption;
                let cls = 'border-slate-200 bg-white';
                let badge: string | null = null;
                if (isCorrect && isSelected) {
                  cls = 'border-green-300 bg-green-50';
                  badge = 'Senin cevabın · Doğru';
                } else if (isCorrect) {
                  cls = 'border-green-300 bg-green-50';
                  badge = 'Doğru cevap';
                } else if (isSelected) {
                  cls = 'border-red-300 bg-red-50';
                  badge = 'Senin cevabın · Yanlış';
                }
                return (
                  <li
                    key={o.key}
                    className={`flex items-start gap-2 rounded border px-2 py-1.5 ${cls}`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        isCorrect
                          ? 'bg-green-700 text-white'
                          : isSelected
                          ? 'bg-red-700 text-white'
                          : 'border border-slate-300 text-slate-600'
                      }`}
                    >
                      {o.key}
                    </span>
                    <span className="flex-1 text-slate-800">{o.text}</span>
                    {badge && (
                      <span className="ml-2 shrink-0 text-xs font-medium text-slate-700">
                        {badge}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!q.answered && (
            <p className="mt-2 text-xs italic text-amber-700">
              Bu soruyu sınav sırasında boş bıraktın.
            </p>
          )}

          {q.explanation && (
            <div className="mt-4 rounded border border-slate-200 bg-white p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Açıklama
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                {q.explanation}
              </p>
            </div>
          )}

          {q.commonMistakes.length > 0 && (
            <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">
                Yaygın Hatalar
              </p>
              <ul className="mt-1 ml-4 list-disc text-sm text-amber-900">
                {q.commonMistakes.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {q.code && (
            <p className="mt-3 font-mono text-xs text-slate-400">{q.code}</p>
          )}
        </div>
      )}
    </div>
  );
}

function CertificateBanner({
  cert,
  candidateName,
}: {
  cert: { id: string; validUntil: string | null };
  candidateName: string;
}): JSX.Element {
  const verifyUrl = `${window.location.origin}/sertifika/${cert.id}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`;
  const validUntil = cert.validUntil ? new Date(cert.validUntil) : null;

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(verifyUrl);
    } catch {
      // ignore
    }
  }

  return (
    <Card className="mt-6 border-2 border-blue-900 bg-blue-50">
      <div className="flex items-start gap-4">
        <div className="text-4xl" aria-hidden="true">
          🏅
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-blue-900">
            Sertifikanızı Kazandınız
          </h2>
          <p className="mt-1 text-sm text-slate-700">
            {candidateName} adına dijital sertifikanız hazır.
            {validUntil && (
              <>
                {' '}
                <span className="text-slate-600">
                  ({validUntil.toLocaleDateString('tr-TR')} tarihine kadar
                  geçerli)
                </span>
              </>
            )}
          </p>

          <div className="mt-3 flex items-center gap-2 rounded-lg bg-white p-3">
            <code className="flex-1 font-mono text-sm tracking-wider text-slate-900">
              {cert.id}
            </code>
            <Link
              href={`/sertifika/${cert.id}`}
              className="rounded-md bg-blue-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-800"
            >
              Doğrula
            </Link>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`/api/public/verify/${cert.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-blue-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800"
            >
              📄 PDF İndir
            </a>
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19 0H5C2.2 0 0 2.2 0 5v14c0 2.8 2.2 5 5 5h14c2.8 0 5-2.2 5-5V5c0-2.8-2.2-5-5-5zM7.5 19h-3v-9h3v9zm-1.5-10.3c-1 0-1.7-.7-1.7-1.7s.7-1.7 1.7-1.7 1.7.7 1.7 1.7-.7 1.7-1.7 1.7zM20 19h-3v-4.4c0-1.4-.6-2.1-1.6-2.1-1.1 0-1.9.7-1.9 2.1V19h-3v-9h3v1.4c.4-.8 1.5-1.6 3-1.6 2.2 0 3.5 1.4 3.5 4V19z" />
              </svg>
              LinkedIn'de Paylaş
            </a>
            <button
              type="button"
              onClick={copyLink}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Linki Kopyala
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
